import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, FileDown } from "lucide-react";

const PLANS = [
  { name: "50m2 Homes", blurb: "Compact one and two-bedroom footprints under 50 square metres — big living, small footprint." },
  { name: "Basic Homes", blurb: "Honest, affordable starter homes with everything you need and nothing you don't." },
  { name: "Concept Homes", blurb: "Architect-led concept designs that push form, light and material further." },
  { name: "Designer Homes", blurb: "Our signature designer series — elevated finishes and considered detailing throughout." },
  { name: "Family Homes II", blurb: "The second edition of our family range — smarter layouts for the way families actually live." },
  { name: "Four Bedroom Homes II", blurb: "Four-bedroom designs, refined — space for everyone without wasted square metres." },
  { name: "Kiwiana Homes", blurb: "Classic Kiwi bach-inspired designs, rebuilt with modern performance and comfort." },
  { name: "Minor Dwellings", blurb: "Secondary dwellings and granny flats — council-savvy designs for your existing section." },
  { name: "Prefab Homes", blurb: "Factory-built in controlled conditions, delivered to site — faster, tighter, tidier." },
  { name: "Three Bedroom Homes", blurb: "Our most popular range — three-bedroom homes tuned for first and forever homes." },
  { name: "Small Homes 3", blurb: "Volume three of our small-footprint series — clever plans that live far bigger." },
  { name: "Under 80 Homes", blurb: "Full-featured homes under 80m² — ideal for downsizers, rentals and rural sites." },
  { name: "Transitional Homes", blurb: "Flexible designs that adapt as life changes — grow into them, not out of them." },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}>
    {children}
  </motion.div>
);

export default function OurPlans() {
  return (
    <main data-testid="our-plans-page" className="bg-white">
      {/* HEADER */}
      <section className="bg-black text-white pt-52 pb-20 sm:pb-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold mb-6">The LW&Co plan range</motion.p>
          <div className="overflow-hidden">
            <motion.h1 initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="font-display font-semibold uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl"
              data-testid="plans-headline">
              Our Plans
            </motion.h1>
          </div>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="mt-8 max-w-xl text-base text-white/60 leading-relaxed">
            Thirteen home ranges, from 50m² minor dwellings to designer family homes — every one
            built by our Masterton crew. Brochure PDFs for each range are on their way.
          </motion.p>
        </div>
      </section>

      {/* CATALOGUE */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PLANS.map((p, i) => (
              <Reveal key={p.name} delay={(i % 3) * 0.08}>
                <article data-testid={`plan-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group h-full border border-black/10 p-8 flex flex-col hover:border-gold hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-start justify-between">
                    <span className="text-[11px] font-bold tracking-[0.3em] text-gold">{String(i + 1).padStart(2, "0")}</span>
                    <span data-testid={`plan-brochure-badge-${i + 1}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1 border border-gold/60 text-gold text-[9px] font-bold uppercase tracking-[0.2em]">
                      <FileDown size={11} /> Brochure PDF — Coming Soon
                    </span>
                  </div>
                  <h2 className="font-display font-semibold uppercase tracking-tight text-2xl sm:text-[1.7rem] mt-6 group-hover:text-gold transition-colors duration-300">
                    {p.name}
                  </h2>
                  <p className="mt-4 text-sm text-black/55 leading-relaxed flex-1">{p.blurb}</p>
                  <Link to={`/contact?topic=${encodeURIComponent("the " + p.name + " range")}`} data-testid={`plan-enquire-${i + 1}`}
                    className="mt-7 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-black hover:text-gold transition-colors w-fit">
                    Enquire about this range <ArrowRight size={13} />
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16 bg-black text-white p-8 sm:p-12 flex flex-wrap items-center justify-between gap-8">
            <div>
              <h2 className="font-display font-semibold uppercase tracking-tight text-2xl sm:text-3xl">
                Want the brochures first?
              </h2>
              <p className="mt-3 text-sm text-white/60 max-w-md leading-relaxed">
                Register your interest and we'll send each range brochure to your inbox the moment it's released.
              </p>
            </div>
            <Link to="/contact?topic=the%20plan%20range%20brochures" data-testid="plans-cta-btn"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-gold text-black font-bold text-sm uppercase tracking-[0.15em] hover:bg-white transition-colors duration-300">
              Register interest <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
